import type { Layout } from 'react-grid-layout';
import type {
  AggregatedPortfolioSettings,
  Analysis,
  AnalysisPortfolioComparisonTypeEnum,
  AnalysisTypeEnum,
  CombinePortfoliosOrganizeOptionEnum,
  CustomBenchmarkTypeEnum,
  FrequencyEnum,
  Fund,
  HoldingsCategoryId,
  ImageDetails,
  Message,
  PeerGroupIdentifier,
  Portfolio,
  PortfolioCompare,
  PrivateFund,
  PrivatePortfolioNode,
  Scenario,
  TaxRate,
  TimeFrame,
} from 'venn-api';
import type {
  CorrelationLabelDisplayType,
  DataGridSizeType,
  EditorValue,
  GeoLevelFilterType,
  Page,
  PortfolioStrategyFilterType,
  MultiLevelPortfolioFilterSelection,
  SectorLevelFilterType,
  LegendPosition,
} from 'venn-components';
import type { DateRange } from 'venn-ui-kit';
import { assertExhaustive } from 'venn-utils';
import type {
  AsOfDateSetting,
  AnalysisSubject,
  CustomizableMetric,
  ExcelCell,
  RowDisplayLimitType,
  SelectableCustomNotablePeriod,
  TabularDataTypeEnum,
} from 'venn-utils';
import type { CustomMetricGrid } from './customFieldTypes';
import type { CustomBlockFonts } from './fonts';

export type StudioExcelFunction = { excelFunction: (() => ExcelCell[][]) | undefined } | undefined;

export type AllocatorConfig = {
  portfolioId: number;
  selectedStrategyId: number | undefined;
  comparisonConfig?: {
    comparisonType: AnalysisPortfolioComparisonTypeEnum;
    comparisonPortfolioId: number;
    comparisonPortfolioVersion: number | undefined;
    comparisonSelectedStrategyId: number | undefined;
  };
};

export type PrivateAllocatorConfig = {
  privatePortfolioId: string;
};

export type ScatterAxisRange = {
  min: number | null;
  max: number | null;
  interval: number | null;
};

export type ScatterAxisConfig = {
  x: ScatterAxisRange;
  y: ScatterAxisRange;
};

export type ScatterAxisStaticHints = {
  zeroBaseline?: 'min' | 'max';
  clipsData?: 'min' | 'max' | 'both';
  dataType?: TabularDataTypeEnum;
};

export type ScatterAxisRenderedExtremes = {
  min: number;
  max: number;
  interval: number;
};

export type ScatterAxisStaticMetadata = {
  x?: ScatterAxisStaticHints;
  y?: ScatterAxisStaticHints;
};

export type ScatterAxisRenderedMetadata = {
  x?: ScatterAxisRenderedExtremes;
  y?: ScatterAxisRenderedExtremes;
};

export type CustomViewOptions = {
  BLOCK_WIDTH?: number;
  allFactorsSelected?: boolean;
  correlationLabelDisplayType?: CorrelationLabelDisplayType;
  customFonts?: CustomBlockFonts;
  customMetricGrid?: CustomMetricGrid;
  customMetricsSettings?: CustomizableMetric[];
  customNotablePeriods?: SelectableCustomNotablePeriod[];
  dataGridSizeType?: DataGridSizeType;
  geoLevelFilter?: GeoLevelFilterType;
  hideBenchmark?: boolean;
  hideBorders?: boolean;
  historicalPortfolioAsOfDate?: [AsOfDateSetting, AsOfDateSetting];
  holdingsCategoryIds?: HoldingsCategoryId[];
  imageId?: string;
  multiLevelPortfolioStrategyFilter?: MultiLevelPortfolioFilterSelection;
  portfolioStrategyFilter?: PortfolioStrategyFilterType;
  privateAssetAllocation?: number;
  privateAssetIsCumulative?: boolean;
  privateDefaultAsOfDateBehavior?: boolean;
  privateProjectFromLastCashFlow?: boolean;
  richText?: EditorValue;
  rowDisplayLimit?: RowDisplayLimitType;
  scenarios?: Scenario[];
  sectionName?: string;
  sectorLevelFilter?: SectorLevelFilterType;
  selectedInvestmentGroupInformation?: string[];
  selectedNotablePeriods?: number[];
  selectedPeerGroupId?: PeerGroupIdentifier;
  showChangesColumn?: boolean;
  showVenncast?: boolean;
  startingNAV?: number;
  subtitle?: string;
  tableSpacingRatio?: number;
  title?: string;
  viewTaxRatesPerSubject?: Record<string, TaxRate>;
  yAxisUnitFormat?: UnitFormat;
  showGainLoss?: boolean;
  showNetTaxImpact?: boolean;
  scatterAxisConfig?: ScatterAxisConfig;
  pieChartLegendPosition?: LegendPosition;
  expandedOriginalLayout?: { readonly x: number; readonly w: number };
  expandedContinuationPageCount?: number;
  continuationUserBlocks?: Record<number, Layout>;
};

export enum UnitFormat {
  ALLOCATION = 0,
  RATIO = 1,
  PERCENT = 2,
}

export type GlobalCustomViewOptions = {
  aggregatedGroupings?: Record<string, CombinePortfoliosOrganizeOptionEnum>;
  allocatorConfig?: AllocatorConfig;
  coverPageImage?: string;
  custom_footer_text?: string;
  hide_footer_disclaimer?: boolean;
  hide_footer_pagination?: boolean;
  hide_header_company_name?: boolean;
  hide_header_logo?: boolean;
  hide_header_view_name?: boolean;
  images?: ImageDetails[];
  pages?: Page[];
  primaryColor?: string;
  privateAllocatorConfig?: PrivateAllocatorConfig;
  sectionImages?: ImageDetails[];
  sectionPageImage?: string;
  studioSectionPageHasFullPageBackground?: boolean;
  studioSectionPageShouldHideCompanyName?: boolean;
  studioSectionTextColor?: string;
  studioTitlePageHasDisclosureTextAlignedToRight?: boolean;
  studioTitlePageHasFullWidthImage?: boolean;
  studioTitlePageHideLogo?: boolean;
  taxRatesPerSubject?: Record<string, TaxRate>;
};

export type Subject =
  | { type: 'fund'; fundId: string }
  | { type: 'private-fund'; privateFundId: string }
  | { type: 'portfolio'; portfolioId: number; portfolioVersion?: number }
  | { type: 'private-portfolio'; privatePortfolioId: string };

export const fundSubject = (fundId: string): Subject => ({ type: 'fund', fundId });

export const privateFundSubject = (privateFundId: string): Subject => ({ type: 'private-fund', privateFundId });

export const portfolioSubject = (portfolioId: number, portfolioVersion?: number): Subject => ({
  type: 'portfolio',
  portfolioId,
  portfolioVersion,
});

export const privatePortfolioSubject = (privatePortfolioId: string): Subject => ({
  type: 'private-portfolio',
  privatePortfolioId,
});

// Re-exported from venn-utils (not defined here) to avoid a circular require: venn-state already depends on
// venn-utils at runtime, so the key's implementation lives there instead of creating the reverse edge.
export { aggregatedSubjectKey } from 'venn-utils';

export const isPrivateSubject = (subject: Subject): boolean =>
  subject.type === 'private-fund' || subject.type === 'private-portfolio';

/**
 * Returns an object containing only subject fields (defined above)
 * Helpful when Subject is used as a key in Recoil atom/selector family
 */
export function toSubjectOnly(subject: Subject): Subject;
export function toSubjectOnly(subject: undefined): undefined;
export function toSubjectOnly(subject: Subject | undefined): Subject | undefined;
export function toSubjectOnly(subject?: Subject): Subject | undefined {
  if (!subject) {
    return undefined;
  }
  switch (subject.type) {
    case 'fund':
      return fundSubject(subject.fundId);
    case 'private-fund':
      return privateFundSubject(subject.privateFundId);
    case 'portfolio':
      return portfolioSubject(subject.portfolioId, subject.portfolioVersion);
    case 'private-portfolio':
      return privatePortfolioSubject(subject.privatePortfolioId);
    default:
      return assertExhaustive(subject);
  }
}

export type SubjectWithOptionalFee = Subject & { feesMapping?: Record<string, number> };

export type SubjectWithOptionalFeeAndFilters = SubjectWithOptionalFee & { filteredSubjectIds?: string[] };

export type SubjectWithFee = Subject & { feesMapping: Record<string, number> };

export type StudioRequestPortfolioCompare = PortfolioCompare & {
  historical: boolean;
};

export type StudioRequestSubject = {
  id: string;
  name: string;
  /**
   * Defined if this subject's portfolio is different than the last saved version of it due to either:
   *   - being modified in the allocator panel
   *   - being a previously saved version of that portfolio
   */
  modifiedPortfolio?: Portfolio;
  modifiedPrivatePortfolio?: PrivatePortfolioNode;
  /**
   * Active benchmark of the subject, if set.
   */
  individualBenchmark?: StudioRequestPortfolioCompare;
  /**
   * A mapping of subject id to management fee
   * A portfolio can have the root set to a given fee, whilst leaf nodes have 0 fees
   * Fees are not supported for private assets
   */
  feesMapping?: { [key: string]: number };
  isFiltered?: boolean;
  /**
   * Portfolio OR fund has to be present (noted in the type concatenation below).
   */
  portfolio?: Portfolio;
  fund?: Fund;
  privatePortfolio?: PrivatePortfolioNode;
  privateFund?: PrivateFund;

  /**
   * Strategy is defined on 'PORTFOLIO' type, if a different strategy than root is selected
   */
  strategy?: Portfolio;
  /**
   * Both `portfolioComparisonType` and `primaryPortfolio` are only defined if the current subject is a comparison
   * subject of another portfolio (set in the allocator).
   */
  portfolioComparisonType?: AnalysisPortfolioComparisonTypeEnum;
  /** Only defined if the current subject is a comparison subject of another portfolio. */
  primaryPortfolio?: Portfolio;
  /** The proxy set ID for tracking analysis views - always required to ensure compiler helps us catch missing assignments */
  proxySetId: string | undefined;
  private: boolean;
  /** Present only for RCM aggregated (combined) portfolios; lets the id-less combined subject round-trip back to an
   * `aggregated-portfolio` Subject instead of degrading to a portfolio with a NaN id. */
  aggregatedPortfolioSettings?: AggregatedPortfolioSettings;
} & (
  | {
      portfolio: Portfolio;
      subjectType: 'PORTFOLIO';
      private: false;
    }
  | {
      fund: Fund;
      subjectType: 'INVESTMENT';
      private: false;
    }
  | {
      privatePortfolio: PrivatePortfolioNode;
      subjectType: 'PORTFOLIO';
      private: true;
    }
  | {
      privateFund: PrivateFund;
      subjectType: 'INVESTMENT';
      private: true;
    }
);

/**
 For holdings tables, we are putting all subjects together in the tables
 We need additional information to pass to figure out which were benchmarks
 */
export type HoldingsRequestSubject = StudioRequestSubject & {
  isBenchmark: boolean;
};
export type SubjectGroupId = string;

export type BlockId = string;

export type BenchmarkConfig = {
  relative: boolean;
  type: CustomBenchmarkTypeEnum;
  subject?: AnalysisSubject;
};

export type StudioAnalysis = Analysis & {
  globalError?: Message;
};
export type AnalysisResult = (StudioAnalysis | undefined)[];

export type AnalysesResult = AnalysisResult[];

export interface StudioAnalysisRequest {
  dateRange: DateRange;
  analysesTypes: AnalysisTypeEnum[];
  subject: StudioRequestSubject;
  benchmark: StudioRequestSubject | undefined;
  /** The type of the {@link StudioAnalysisRequest.benchmark} on this subject. */
  benchmarkType: CustomBenchmarkTypeEnum;
  relative: boolean;
  frequency: FrequencyEnum;
  rollingYears?: number;
  /** True if this request is for benchmark data. */
  isBenchmark?: boolean;
  selectedNotablePeriods?: number[];
  includeAllPredefinedPeriods?: boolean;
  customTimeFrames?: TimeFrame[];
  forecastWindow?: number;
  initialNavAmount?: number;
  forecastConfidenceLevels?: number[];
  scenarios?: Scenario[];
  groupRootLevelInvestments?: boolean;
  peerGroupIdentifier?: PeerGroupIdentifier;
  returnsBasedSubjectStartingNav?: number;
  historicalPortfolioAsOfDate?: number;
  taxRate?: TaxRate;
}

export enum StudioExcelExportKeys {
  PROXY = 'proxy-export',
  VDPAR = 'vdpar-export',
}
